import * as React from 'react';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useFormik } from 'formik';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';

import {
  GetEventDetailedDocument,
  useUpdateEventMutation,
  useListEventCategoriesQuery,
  GetEventDetailedQuery,
} from '@graphql';
import { useSession, useToastError } from '@hooks';
import initValues, { getMinimalFromDate } from './initValues';
import validationSchema from './validationSchema';
import { Form } from '../../form';
import { WrapperContext } from '../../common/layout';

interface EventPreviewManageProps {
  event: GetEventDetailedQuery['event'];
}

export function EventPreviewManage(props: EventPreviewManageProps) {
  const context = React.useContext(WrapperContext);

  if (!context) {
    throw new Error('EventPreviewManage must be used inside Wrapper');
  }

  const { isManaging, setIsManaging, isSaving, setIsSaving, isSaveDisabled, setIsSaveDisabled } = context;

  const { event } = props;
  const { account } = useSession();
  const toastError = useToastError();
  const { t } = useTranslation('field');

  const defaultValues = initValues(event);

  const formik = useFormik({
    initialValues: defaultValues,
    validationSchema: validationSchema(t),
    onSubmit: (values) => {
      // only send changed values to the API
      const update = {
        title: values.title !== defaultValues.title ? values.title : undefined,
        from: values.from && !moment(values.from).isSame(defaultValues.from) ? values.from : undefined,
        to: values.to && !moment(values.to).isSame(defaultValues.to) ? values.to : undefined,
        categoryId: values.categoryId !== defaultValues.categoryId ? values.categoryId : undefined,
        isPrivate: values.isPrivate !== defaultValues.isPrivate ? values.isPrivate : undefined,
      };

      updateEvent({
        variables: {
          input: {
            ...update,
            id: event.id,
            categoryId: update.categoryId?.length ? update.categoryId : null,
          },
        },
      });
    },
  });

  const { data } = useListEventCategoriesQuery();

  const [updateEvent, { loading: updating }] = useUpdateEventMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GetEventDetailedDocument, variables: { id: event.id } }],
    onCompleted: ({ updateEvent }) => {
      toast(t('event.toast.update.success', { ns: 'field' }), { type: 'success' });
      setIsSaving(false);
      setIsManaging(null);
    },
    onError(error) {
      toastError(error, t('event.toast.update.error', { ns: 'field' }));
    },
  });

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', 'Escape'].includes(event.key)) {
      (event.target as HTMLElement).blur();
    }
  };

  React.useEffect(() => {
    setIsSaving(updating);
  }, [updating]);

  React.useEffect(() => {
    if (isSaving) {
      formik.submitForm();
    }
  }, [isSaving]);

  React.useEffect(() => {
    if (!isManaging) {
      formik.resetForm({ values: defaultValues });
    }
  }, [isManaging]);

  React.useEffect(() => {
    if (isSaveDisabled && formik.isValid && formik.dirty) {
      setIsSaveDisabled(false);
    } else if (!isSaveDisabled && (!formik.isValid || !formik.dirty)) {
      setIsSaveDisabled(true);
    }
  }, [isSaveDisabled, formik.isValid, formik.dirty]);

  if (!data?.eventCategories?.length) {
    return null;
  }

  return (
    <div>
      <Form.Body sx={{ m: 0, p: 0 }}>
        {account?.__typename === 'Spot' && (
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  required
                  name="isPrivate"
                  checked={formik.values.isPrivate}
                  onChange={formik.handleChange}
                  slotProps={{ input: { 'aria-label': '' } }}
                  color="error"
                />
              }
              label={t(`event.label.isPrivate.${formik.values.isPrivate}`)}
            />
          </FormGroup>
        )}
        <TextField
          required
          label={t('event.label.title')}
          name="title"
          value={formik.values.title}
          onChange={formik.handleChange}
          onKeyDown={handleOnKeyDown}
          error={formik.touched.title && Boolean(formik.errors.title)}
          helperText={formik.touched.title && formik.errors.title ? String(formik.errors.title) : null}
          fullWidth
        />
        <Stack direction="row" spacing={2}>
          <DateTimePicker
            label={t('event.label.from') + ' *'}
            value={formik.values.from}
            onChange={(value) => {
              formik.setFieldValue('from', value);
              formik.setFieldTouched('from', !moment(value).isSame(formik.values.from));
            }}
            disablePast
            slotProps={{
              textField: {
                name: 'from',
                error: formik.touched.from && Boolean(formik.errors.from),
                helperText:
                  formik.touched.from && formik.errors.from ? String(formik.errors.from) : undefined,
                fullWidth: true,
              },
            }}
          />
          <DateTimePicker
            label={t('event.label.to') + ' *'}
            value={formik.values.to}
            onChange={(value) => {
              formik.setFieldValue('to', value);
              formik.setFieldTouched('to', !moment(value).isSame(formik.values.to));
            }}
            disablePast
            slotProps={{
              textField: {
                name: 'to',
                error: formik.touched.to && Boolean(formik.errors.to),
                helperText: formik.touched.to && formik.errors.to ? String(formik.errors.to) : undefined,
                fullWidth: true,
              },
            }}
          />
        </Stack>
        <FormControl
          required
          fullWidth
          error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
        >
          <InputLabel id="category-select-label">{t(`event.label.category`)}</InputLabel>
          <Select
            required
            labelId="category-select-label"
            id="category-select"
            value={formik.values.categoryId || ''}
            name="categoryId"
            label="Category"
            onChange={formik.handleChange}
          >
            {[
              { id: '', name: t(`event.placeholder.category`) },
              ...data.eventCategories.map((category) => ({
                id: category.id,
                name: t(`category.${category.name}`, { ns: 'event' }),
              })),
            ].map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {formik.errors.categoryId ? String(formik.errors.categoryId) : undefined}
          </FormHelperText>
        </FormControl>
      </Form.Body>
    </div>
  );
}
